import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TestGraphPage from './TestGraphPage';
import { graphBuilderApi } from '../../services/api';
import './EmbedTestGraph.css';

/**
 * ChatAssistantPublicPage
 *
 * Public, chrome-less host for a published Chat Assistant, mounted at
 * /chatassist/:uuid (OUTSIDE the /user-home ProtectedRoute). It:
 *
 *   1. Fetches presentation-only config from
 *      GET /api/chatassist/public/:uuid (title, welcome, suggested questions,
 *      streaming flag). It never receives SNN settings or the owner account.
 *   2. Adds the ``cm-embed`` class to <html>/<body> so the shared stylesheet
 *      suppresses all CerebralMachines chrome (same mechanism as the embed
 *      page).
 *   3. Renders TestGraphPage in "assistant mode" -- every run is proxied
 *      through POST /api/chatassist/:uuid/run(/stream), which rebuilds the
 *      locked pipeline request server-side and bills the publisher account.
 */
export default function ChatAssistantPublicPage() {
  const { uuid } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.add('cm-embed');
    document.documentElement.classList.add('cm-embed');
    return () => {
      document.body.classList.remove('cm-embed');
      document.documentElement.classList.remove('cm-embed');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await graphBuilderApi.getChatAssistantPublic(uuid);
        if (!cancelled) setConfig(resp);
      } catch (err) {
        if (!cancelled) {
          const status = err.response?.status;
          setError(
            status === 404
              ? 'This chat assistant is not available. It may have been unpublished or the link is incorrect.'
              : (err.message || 'Failed to load chat assistant.'),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uuid]);

  if (loading) {
    return (
      <div className="cm-embed-shell cm-embed-shell--loading">
        <div className="cm-embed-spinner" />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="cm-embed-shell cm-embed-shell--error">
        <h2>Chat unavailable</h2>
        <p>{error || 'Chat assistant not found.'}</p>
      </div>
    );
  }

  return (
    <div className="cm-embed-shell" key={uuid}>
      <TestGraphPage assistantUuid={uuid} assistantPublicConfig={config} />
    </div>
  );
}
